/* eslint-disable no-restricted-globals */
import { RouteComponentProps } from "@reach/router";
import {
  FormEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";

import Button from "../components/button/Button";
import Table, { Column } from "../components/table/Table";
import LayoutContext from "../context/layout-context";
import { initialPaginationData } from "../utils/constants";
import ContextMenu, {
  ContextOption
} from "../components/context-menu/ContextMenu";
import { BooleanFilter } from "../utils/helpers/types-helpers";
import Modal, { ModalProps } from "../components/modal/Modal";
import { resources } from "../utils/resources";
import Input from "../components/input/Input";
import {
  ApplicationCreate,
  ApplicationCreations
} from "../utils/helpers/types";

import styles from "./self-service.module.scss";
import {
  createApplication,
  getApplication,
  getApplications,
  updateApplication
} from "../utils/helpers/data/application";

const Application: FunctionComponent<RouteComponentProps> = () => {
  const [loading, setLoading] = useState(false);
  const [reset, setReset] = useState(false);
  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [applicationOnView, setApplicationOnView] =
    useState<ApplicationCreations | null>(null);
  const { notify } = useContext(LayoutContext);
  const [application, setApplication] = useState<ApplicationCreations[]>([]);
  const [applicationOnEdit, setApplicationOnEdit] =
    useState<ApplicationCreations | null>(null);
  const [showAddEditModal, setShowAddEditModal] = useState(false);

  const displayAddEditModal = async (
    applicationCreation: ApplicationCreations | null
  ) => {
    if (applicationCreation) {
      setLoading(true);
      const { data, error, message } = await getApplication(
        applicationCreation.id
      );
      setLoading(false);
      if (error || !data) {
        notify("error", `Unable to fetch application details: ${message}`);
        return;
      }
      setApplicationOnEdit(data);
    } else {
      setApplicationOnEdit(null);
    }
    setShowAddEditModal(true);
  };

  const dismissAddEditModal = () => {
    setApplicationOnEdit(null);
    setShowAddEditModal(false);
  };

  const fetchApplication = async (
    pageNumber = pagination.pageNumber,
    pageSize = pagination.pageSize
  ) => {
    setLoading(true);
    const { data, error, message } = await getApplications(
      pageNumber,
      pageSize
    );
    setLoading(false);

    if (error || !data) {
      notify("error", `Unable to fetch application: ${message}`);
      return;
    }

    setApplication(data.records);
    setPagination({
      ...pagination,
      pageNumber,
      total: data.total
    });

    setReset(false);
  };

  useEffect(() => {
    fetchApplication();
  }, [pagination.pageSize]);

  useEffect(() => {
    if (!reset) {
      fetchApplication();
    } else {
      setPagination({ ...initialPaginationData });
      setReset(false);
    }
  }, [reset]);

  const handleOperation = async (operation: any, id: any) => {
    setLoading(true);
    let result;

    switch (operation) {
      case "view":
        result = await getApplication(id);
        if (!result.error) {
          setApplicationOnView(result.data);
        }
        break;
      default:
        setLoading(false);
        return;
    }

    setLoading(false);

    if (result.error) {
      notify("error", `Unable to ${operation} application: ${result.message}`);
      return;
    }

    notify("success", `application ${operation}d successfully`);
  };

  const formatDate = (date: string): string => {
    const formattedDate = new Date(date)
      .toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      })
      .replace(/\//g, "-");
    return formattedDate;
  };
  const columns: Column<ApplicationCreations>[] = [
    {
      title: "Application Name",
      dataIndex: "applicationName",
      key: "applicationName"
    },
    {
      title: "Application Id",
      dataIndex: "applicationId",
      key: "applicationId"
    },
    {
      title: "Client Id",
      dataIndex: "clientIdColumnName",
      key: "clientIdColumnName"
    },
    {
      title: "Service Id",
      dataIndex: "serviceIdColumnName",
      key: "serviceIdColumnName"
    },
    {
      title: "Extraction Cycle",
      dataIndex: "dataExtractionCycle",
      key: "dataExtractionCycle"
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      render: (createdDate: string) => formatDate(createdDate)
    },

    {
      title: "",
      render: (_: unknown, application: ApplicationCreations) => {
        const options: ContextOption[] = [
          {
            label: "View Details",
            onClick: () => {
              handleOperation("view", application.id);
            }
          },
          {
            label: "Edit",
            onClick: () => {
              displayAddEditModal(application);
            }
          }
        ].filter(BooleanFilter) as ContextOption[];
        return <ContextMenu options={options} />;
      }
    }
  ];
  return (
    <main>
      <h1 className="title vertical-margin spaced thin">Application</h1>

      <br />

      <div className="vertical-margin flex end">
        <Button onClick={() => displayAddEditModal(null)}>
          Create Application
        </Button>
      </div>

      <Table
        dataSource={application || []}
        columns={columns}
        pagination={pagination}
        onPageChange={fetchApplication}
        onPageSizeChange={pageSize =>
          setPagination({ ...pagination, pageSize, pageNumber: 1 })
        }
        loading={loading}
        className="smaller"
      />

      <ClientViewModal
        application={applicationOnView}
        cancel={() => setApplicationOnView(null)}
        visible={Boolean(applicationOnView)}
      />

      <AddEditModal
        visible={showAddEditModal}
        applicationOnEdit={applicationOnEdit}
        cancel={dismissAddEditModal}
        refresh={(stayOnPage: any) =>
          fetchApplication(stayOnPage ? pagination.pageNumber : 1)
        }
        fetchData={fetchApplication}
      />
    </main>
  );
};

const blankappService: ApplicationCreate = {
  applicationId: "",
  applicationName: "",
  databaseUrl: "",
  databaseUser: "",
  databasePassword: "",
  databaseDriver: "",
  logTableName: "",
  clientIdColumnName: "",
  serviceIdColumnName: "",
  requestDateColumnName: "",
  requestIdColumnName: "",
  responseCodeColumnName: "",
  successfulStatusCodes: "",
  excludeResponseCode: true,
  singleService: false,
  additionalFilters: "",
  nextDataExtractionDate: "",
  lastDataExtractionDate: "",
  dataExtractionCycle: ""
};

const AddEditModal: FunctionComponent<
  ModalProps & {
    applicationOnEdit: ApplicationCreations | null;
    refresh: (stayOnPage: boolean) => void;
    cancel: () => void;
    fetchData: () => void;
  }
> = ({ visible, cancel, applicationOnEdit, refresh, fetchData }) => {
  const [formData, setFormData] = useState<ApplicationCreate>({
    ...blankappService,
    ...applicationOnEdit
  });

  const [isSaving, setIsSaving] = useState(false);
  const { notify } = useContext(LayoutContext);

  const handleChange = (key: keyof ApplicationCreate, value: unknown) => {
    setFormData(prevFormData => ({ ...prevFormData, [key]: value }));
  };

  useEffect(() => {
    setFormData(
      applicationOnEdit
        ? { ...blankappService, ...applicationOnEdit }
        : { ...blankappService }
    );
  }, [visible, applicationOnEdit]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    setIsSaving(true);

    let id: number | undefined;

    if (applicationOnEdit && typeof applicationOnEdit.id === "number") {
      id = applicationOnEdit.id;
    }

    if (id !== undefined) {
      const { error, message } = await updateApplication(id, {
        ...formData
      });

      if (error) {
        notify("error", `Unable to update application: ${message}`);
        setIsSaving(false);
        return;
      }

      notify("success", "Successfully updated application");
    } else {
      const { error, message } = await createApplication({
        ...formData
      });

      if (error) {
        notify("error", `Unable to create application: ${message}`);
        setIsSaving(false);
        return;
      }

      notify("success", "Successfully created application");
    }
    cancel?.();
    refresh(false);
    setIsSaving(false);
    fetchData();
  };

  return (
    <Modal cancel={cancel} visible={visible} size="small">
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <span className="question">Application Id</span>
          <Input
            value={formData.applicationId}
            onChange={value => handleChange("applicationId", value)}
            placeholder="Enter application id"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Application name</span>
          </div>
          <Input
            value={formData.applicationName}
            onChange={value => handleChange("applicationName", value)}
            placeholder="Enter application name"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Data base url</span>
          </div>
          <Input
            value={formData.databaseUrl}
            onChange={value => handleChange("databaseUrl", value)}
            placeholder="Enter database url"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Database user</span>
          </div>
          <Input
            value={formData.databaseUser}
            onChange={value => handleChange("databaseUser", value)}
            placeholder="Enter database user"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Database password</span>
          </div>
          <Input
            value={formData.databasePassword}
            onChange={value => handleChange("databasePassword", value)}
            placeholder="Enter database password"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Database driver</span>
          </div>
          <Input
            value={formData.databaseDriver}
            onChange={value => handleChange("databaseDriver", value)}
            placeholder="Enter database driver"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Log table name</span>
          </div>
          <Input
            value={formData.logTableName}
            onChange={value => handleChange("logTableName", value)}
            placeholder="Enter log table name"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Client Id column name</span>
          </div>
          <Input
            value={formData.clientIdColumnName}
            onChange={value => handleChange("clientIdColumnName", value)}
            placeholder="Enter client Id column name"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Service Id column name</span>
          </div>
          <Input
            value={formData.serviceIdColumnName}
            onChange={value => handleChange("serviceIdColumnName", value)}
            placeholder="Enter Service id column name"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Request date column name</span>
          </div>
          <Input
            value={formData.requestDateColumnName}
            onChange={value => handleChange("requestDateColumnName", value)}
            placeholder="Enter request date column name"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Request Id column name</span>
          </div>
          <Input
            value={formData.requestIdColumnName}
            onChange={value => handleChange("requestIdColumnName", value)}
            placeholder="Enter request Id column name"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Response code column name</span>
          </div>
          <Input
            value={formData.responseCodeColumnName}
            onChange={value => handleChange("responseCodeColumnName", value)}
            placeholder="Enter response code column name"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Successful Status Codes</span>
          </div>
          <Input
            value={formData.successfulStatusCodes}
            onChange={value => handleChange("successfulStatusCodes", value)}
            placeholder="Enter successful status codes"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Exclude response code</span>
          </div>
          <Input
            value={formData.excludeResponseCode.toString()}
            onChange={value => handleChange("excludeResponseCode", value)}
            placeholder="Enter exclude response code"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Single service</span>
          </div>
          <Input
            value={formData.singleService.toString()}
            onChange={value => handleChange("singleService", value)}
            placeholder="Enter single service"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Next data extraction date</span>
          </div>
          <Input
            value={formData.nextDataExtractionDate}
            onChange={value => handleChange("nextDataExtractionDate", value)}
            placeholder="Enter next data extraction date"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Last data extraction date</span>
          </div>
          <Input
            value={formData.lastDataExtractionDate}
            onChange={value => handleChange("lastDataExtractionDate", value)}
            placeholder="Enter last data extraction date"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Last Bill Date</span>
          </div>
          <Input
            value={formData.dataExtractionCycle}
            onChange={value => handleChange("dataExtractionCycle", value)}
            placeholder="Enter data extraction cycle"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Additional filters</span>
          </div>
          <Input
            value={formData.additionalFilters}
            onChange={value => handleChange("additionalFilters", value)}
            placeholder="Enter additional filters"
            responsive
            required
          />
        </div>

        <br />
        <div className="flex spaced end vertical-margin">
          <Button type="accent" onClick={() => cancel?.()}>
            Cancel
          </Button>
          <Button buttonType="submit" loading={isSaving}>
            {applicationOnEdit ? "Edit application" : "Create application"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

interface clientModalProps {
  application: ApplicationCreations | null;
  cancel: () => void;
  visible: boolean;
}

const ClientViewModal: FunctionComponent<clientModalProps> = ({
  application: _wallet,
  cancel,
  visible
}) => {
  const [loading] = useState(false);
  const [application, setApplication] = useState<ApplicationCreations | null>(
    _wallet
  );

  useEffect(() => {
    setApplication(_wallet);
  }, [_wallet]);

  const formatDate = (date: string): string => {
    const formattedDate = new Date(date)
      .toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      })
      .replace(/\//g, "-");
    return formattedDate;
  };

  return (
    <Modal visible={visible} cancel={cancel} ignoreBackdropClicks>
      {loading ? (
        <img
          alt="loading"
          src={resources.images.LOADING_TYPING}
          className="generic-icon xxl"
        />
      ) : (
        <section className={styles.details}>
          <div className={styles.detail}>
            <span className={styles.key}>Id:</span>
            <span className={styles.value}>{application?.id || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Created Date:</span>
            <span className={styles.value}>
              {application?.createdDate
                ? formatDate(application.createdDate)
                : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Updated date:</span>
            <span className={styles.value}>
              {application?.updatedDate
                ? formatDate(application.updatedDate)
                : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Application Id:</span>
            <span className={styles.value}>
              {application?.applicationId || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Application Name:</span>
            <span className={styles.value}>
              {application?.applicationName || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Database Url:</span>
            <span className={styles.value}>
              {application?.databaseUrl || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Database User:</span>
            <span className={styles.value}>
              {application?.databaseUser || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Database Password:</span>
            <span className={styles.value}>
              {application?.databasePassword || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Database Driver:</span>
            <span className={styles.value}>
              {application?.databaseDriver || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Log Table Name:</span>
            <span className={styles.value}>
              {application?.logTableName || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>ClientId Column Name:</span>
            <span className={styles.value}>
              {application?.clientIdColumnName || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Req Date Column Name: </span>
            <span className={styles.value}>
              {application?.requestDateColumnName || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Req Id Column Name: </span>
            <span className={styles.value}>
              {application?.requestIdColumnName || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Res Code Column Name: </span>
            <span className={styles.value}>
              {application?.responseCodeColumnName || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Successful Status Codes: </span>
            <span className={styles.value}>
              {application?.successfulStatusCodes || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Exclude Response Code:</span>
            <span className={styles.value}>
              {application?.excludeResponseCode.toString() || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Single Service:</span>
            <span className={styles.value}>
              {application?.singleService.toString() || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Next Data Extraction Date:</span>
            <span className={styles.value}>
              {application?.nextDataExtractionDate
                ? formatDate(application.nextDataExtractionDate)
                : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Last Data Extraction Date:</span>
            <span className={styles.value}>
              {application?.lastDataExtractionDate
                ? formatDate(application.lastDataExtractionDate)
                : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Additional Filters:</span>
            <span className={styles.value}>
              {application?.additionalFilters || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Data Extraction Cycle:</span>
            <span className={styles.value}>
              {application?.dataExtractionCycle || "N/A"}
            </span>
          </div>
        </section>
      )}
    </Modal>
  );
};
export default Application;
